import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Autocomplete,
  TextField,
  useTheme,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  ListItemIcon,
} from '@mui/material';
import first from 'lodash/first';
import { Plus as AddIcon } from 'react-feather';

import { plannerSearchQuery } from '@pv/common/graphql';
import {
  PlannerSearchQuery,
  PlannerSearchQueryVariables,
} from '@pv/common/graphql/operations';
import { phonePrint } from '@pv/common/utils';
import { useVenues } from '../providers/venues';

interface Planner {
  id: string;
  fullName?: string | null;
  phone?: string | null;
  email?: string | null;
}

export const SearchContact = ({
  onClickSearchResult,
  onClickAddContact,
  eventId,
}: {
  onClickSearchResult: (plannerId: string) => void;
  onClickAddContact: (firstName: string) => void;
  eventId: string;
}) => {
  const [query, setQuery] = useState('');
  const theme = useTheme();
  const { venues } = useVenues();

  const organizationId = first(venues)?.organization?.id;
  const variables = {
    organizationId: organizationId || '',
    query,
    eventId,
    first: 4,
  };

  const skip = !organizationId || query === '';

  const { data: searchData, loading } = useQuery<
    PlannerSearchQuery,
    PlannerSearchQueryVariables
  >(plannerSearchQuery, { variables, skip, fetchPolicy: 'network-only' });
  const plannerSearch = (searchData?.plannerSearch?.nodes || []).filter(
    (planner): planner is NonNullable<typeof planner> => planner != null
  );

  // Create options array with planners and the "add new contact" option
  const options = [
    ...plannerSearch,
    // Add a special "add new contact" option at the end
    {
      id: 'add-new-contact',
      fullName: query ? `Add "${query}" as new contact` : 'Add new contact',
      isAddNew: true,
    } as Planner & { isAddNew: boolean },
  ];

  const handleOptionSelect = (option: Planner | null) => {
    if (!option) return;

    if ('isAddNew' in option && option.isAddNew) {
      onClickAddContact(query);
    } else {
      onClickSearchResult(option.id);
    }
  };

  return (
    <Autocomplete
      id="search-contact-input"
      freeSolo
      autoComplete
      autoFocus
      fullWidth
      options={options}
      loading={loading}
      value={query}
      onChange={(_, option) => {
        if (typeof option === 'string') {
          setQuery(option);
        } else if (option) {
          handleOptionSelect(option);
        }
      }}
      onInputChange={(_, newInputValue) => {
        setQuery(newInputValue);
      }}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.fullName || '';
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof value === 'string') return option.fullName === value;
        return option.id === value.id;
      }}
      filterOptions={(options) => options} // Don't filter, show all options
      renderOption={(props, option) => {
        if ('isAddNew' in option && option.isAddNew && query) {
          return (
            <>
              <Divider />
              <ListItem
                role="option"
                {...props}
                data-cy="contacts-dropdown-create-contact-button"
              >
                <ListItemIcon>
                  <AddIcon size={24} />
                </ListItemIcon>
                <ListItemText>Create new contact named {query}</ListItemText>
              </ListItem>
            </>
          );
        }

        return (
          <ListItem {...props} key={option.id}>
            <ListItemText>
              <div>{option.fullName}</div>
              <Typography color="textSecondary">
                {option.phone && phonePrint(option.phone)}{' '}
                {option.email && `(${option.email})`}
              </Typography>
            </ListItemText>
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Add Contact"
          name="addContact"
          required
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
          }}
        />
      )}
      sx={{
        '& .MuiAutocomplete-listbox': {
          zIndex: theme.zIndex.modal + 100,
        },
      }}
    />
  );
};
